import { useSyncExternalStore } from "react";

/**
 * Installing StrategyHive as an app (a PWA).
 *
 * Chrome, Edge and Android fire `beforeinstallprompt` once, early, when the site
 * qualifies; if nobody is listening it is gone. So this module listens from the
 * moment the app loads (main.tsx imports it first) and keeps the event, and the
 * Download page offers a button that replays it. Safari and Firefox never fire it;
 * the page explains their way instead.
 */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    emit();
  });
}

/** Running as an installed app right now (its own window, no browser bar). */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function snapshot(): "installed" | "available" | "unavailable" {
  if (installed || isStandalone()) return "installed";
  return deferred ? "available" : "unavailable";
}

export function useInstallState() {
  const state = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    snapshot,
    () => "unavailable" as const,
  );
  return {
    state,
    /** Show the browser's own install dialog. Resolves with what the person chose. */
    install: async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
      if (!deferred) return "unavailable";
      const e = deferred;
      deferred = null;
      await e.prompt();
      const { outcome } = await e.userChoice;
      if (outcome === "accepted") installed = true;
      emit();
      return outcome;
    },
  };
}
